/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 */

import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from 'rxjs';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';

import {ToastService, IxModule} from '@siemens/ix-angular';
import {TranslateService, TranslateModule} from '@ngx-translate/core';
import {Submodel} from '@product-passport/dpp/model';

import {getSubmodelMetadata} from '../../../../../models/dpp/submodel-types';
import {DppService} from '../../../../../services/dpp.service';
import {DppSubmodelFieldComponent} from '../../dpp-submodel-field/dpp-submodel-field.component';
import {LocalizationHelper} from '../../../../shared/utils/localization-helper';

@Component({
  selector: 'app-public-generic-submodel-viewer',
  templateUrl: './public-generic-submodel-viewer.component.html',
  styleUrl: './public-generic-submodel-viewer.component.scss',
  standalone: true,
  imports: [IxModule, TranslateModule, DppSubmodelFieldComponent, RouterLink],
})
export class PublicGenericSubmodelViewerComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dppService = inject(DppService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly localizationHelper = inject(LocalizationHelper);

  private routeSubscription?: Subscription;

  dppId = '';
  submodelId = '';
  shellName = '';

  submodel?: Submodel;

  loading = true;
  passFound = true;

  get submodelVersion(): string | undefined {
    const semanticId = this.submodel?.semanticId?.keys?.[0]?.value;
    return getSubmodelMetadata(semanticId)?.version;
  }

  get submodelName(): string {
    const semanticId = this.submodel?.semanticId?.keys?.[0]?.value;
    const metadata = getSubmodelMetadata(semanticId);
    if (metadata) {
      return this.translate.instant(metadata.type);
    }
    return this.localizationHelper.getLocalizedText(
      this.submodel?.displayName,
      this.submodel?.idShort ?? this.submodelId,
    );
  }

  ngOnInit(): void {
    this.routeSubscription = this.route.params.subscribe(async (params) => {
      this.dppId = params['aasId'];
      this.submodelId = params['submodelId'];
      await this.loadSubmodelData();
    });
  }

  ngOnDestroy(): void {
    this.routeSubscription?.unsubscribe();
  }

  private async loadSubmodelData(): Promise<void> {
    try {
      this.submodel = await this.dppService.getSubmodelPublic(this.submodelId);
      this.passFound = true;
    } catch {
      this.passFound = false;
      await this.toastService.show({
        message: this.translate.instant(
          'dpp.passport_detail_view.load_error_message',
        ),
        type: 'error',
      });
    } finally {
      this.loading = false;
    }

    this.dppService
      .getDppShellPublic(this.dppId)
      .then((shell) => {
        this.shellName = this.localizationHelper.getLocalizedText(
          shell.displayName,
          this.dppId,
        );
      })
      .catch(() => {
        this.shellName = this.dppId;
      });
  }

  onBackButtonClick(): void {
    this.router.navigate(['/public/dpp', this.dppId]);
  }
}
